import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { db } from '../data/store';
import { collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy, newId } from '@battleops/localstore';

const ProjectContext = createContext();
const PROJECTS_COLLECTION = 'projects';

const initialState = {
  projects: [],
  activeProjectId: null, // null = "All Tasks"
  loaded: false,
};

function projectReducer(state, action) {
  switch (action.type) {
    case 'LOAD_PROJECTS':
      return { ...state, projects: action.projects, loaded: true };
    case 'SET_ACTIVE':
      return { ...state, activeProjectId: action.id };
    default:
      return state;
  }
}

export function ProjectProvider({ children }) {
  const [state, dispatch] = useReducer(projectReducer, initialState);

  useEffect(() => {
    const q = query(collection(db, PROJECTS_COLLECTION), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const projects = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      dispatch({ type: 'LOAD_PROJECTS', projects });
    }, () => {
      dispatch({ type: 'LOAD_PROJECTS', projects: [] });
    });
    return () => unsub();
  }, []);

  const addProject = useCallback((project) => {
    const id = project.id || newId();
    const newProject = {
      id,
      name: project.name,
      color: project.color || '#3B82F6',
      icon: project.icon || '📁',
      description: project.description || '',
      address: project.address || '',
      addressFromCustomer: !!project.addressFromCustomer,
      customerId: project.customerId || null,        // a BattleBooks customer (customers → opsCustomers)
      customerName: project.customerName || null,    // shown before the customer list has synced
      startDate: project.startDate || null,
      endDate: project.endDate || null,
      hourlyRate: project.hourlyRate || 0,
      budget: project.budget || 0,
      value: project.value || 0,                     // what the job pays; budget = what we plan to spend
      // null = inherit the department's default (resolveBillable); true/false = this project decides.
      billable: project.billable === true || project.billable === false ? project.billable : null,
      status: 'active',
      departmentId: project.departmentId || null,
      typeId: project.typeId || null,
      folderId: project.folderId || null,
      createdAt: Date.now(),
    };
    setDoc(doc(db, PROJECTS_COLLECTION, id), newProject);
    return newProject;
  }, []);

  const updateProject = useCallback((updates) => {
    const existing = state.projects.find(p => p.id === updates.id);
    if (existing) {
      const merged = { ...existing, ...updates };
      setDoc(doc(db, PROJECTS_COLLECTION, updates.id), merged);
    }
  }, [state.projects]);

  const deleteProject = useCallback((id) => {
    deleteDoc(doc(db, PROJECTS_COLLECTION, id));
    if (state.activeProjectId === id) {
      dispatch({ type: 'SET_ACTIVE', id: null });
    }
  }, [state.activeProjectId]);

  const setActiveProject = useCallback((id) => {
    dispatch({ type: 'SET_ACTIVE', id });
  }, []);

  return (
    <ProjectContext.Provider value={{
      projects: state.projects,
      activeProjectId: state.activeProjectId,
      loaded: state.loaded,
      addProject,
      updateProject,
      deleteProject,
      setActiveProject,
    }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProjects must be used within ProjectProvider');
  return context;
}
